<script setup>
import { ref } from "vue";
import { RouterLink, useRouter } from "vue-router";
import qr1 from "@/assets/qr1.png";
import qr2 from "@/assets/qr2.png";
import logo from "@/assets/logo.png";
import phoneIcon from "@/assets/phone-icon.svg";
import emailIcon from "@/assets/email-icon.svg";
import addressIcon from "@/assets/address-icon.svg";
import institutionIcon from "@/assets/institution-icon.png";
import tabConfig from "@/config/tabConfig";

const router = useRouter();

// 定义菜单名称映射
const menuNameMap = {
  about: "关于研究院",
  research: "科学研究",
  education: "人才培养",
  innovation: "创新基地",
  party: "党群文化",
  hr: "人力资源",
};

// 从 tabConfig 构建菜单项
const menuItems = ref([
  { name: "网站首页", path: "/" },
  ...Object.entries(tabConfig)
    .map(([key, section]) => {
      // 只处理主要导航项
      if (Object.keys(menuNameMap).includes(key)) {
        const path = `/${key}`;
        const query = {};

        // 如果有 overview 子标签，使用其第一项
        if (section.subTabs?.overview?.[0]) {
          query.subTab = "overview";
          query.subSubTab = section.subTabs.overview[0].title;
          query.categoryId = section.subTabs.overview[0].categoryId;
        } else if (section.mainTabs?.[0]) {
          // 否则使用第一个主标签
          query.subTab = section.mainTabs[0].value;
          query.categoryId = section.mainTabs[0].categoryId;
        }

        return {
          name: menuNameMap[key],
          path,
          query,
        };
      }
      return null;
    })
    .filter(Boolean),
]);

const handleNavClick = (item, e) => {
  e.preventDefault();
  router.push({
    path: item.path,
    query: item.query,
  });
  window.scrollTo({
    top: 0,
    behavior: "smooth",
  });
};

const contactInfo = ref({
  phone: {
    business: "业务部门：0471-3121519",
    management: "综合管理部：0471-3121516",
  },
  email: "邮箱：sjtunmg@sjtu.edu.cn",
  address:
    "内蒙古自治区呼和浩特市如意工业园区水岸小镇G-2区G1栋上海交通大学呼和浩特科技创新中心",
});
</script>

<template>
  <footer class="footer">
    <div class="footer-content">
      <div class="top-section">
        <div class="logo">
          <img :src="logo" alt="logo" />
        </div>
        <div class="institution">
          <img :src="institutionIcon" alt="institution" />
          <p class="icp"><a href="https://beian.miit.gov.cn/" target="_blank">蒙ICP备32882132</a></p>
        </div>
      </div>

      <div class="divider"></div>

      <div class="nav-section">
        <div class="nav-section-left">
          <!-- <nav class="footer-nav">
            <a
              v-for="item in menuItems"
              :key="item.path"
              :href="item.path"
              class="nav-item"
              @click="(e) => handleNavClick(item, e)"
            >
              {{ item.name }}
            </a>
          </nav> -->
          <div class="info-section">
            <div class="contact-info">
              <div class="contact-row">
                <img :src="phoneIcon" alt="phone" class="icon" />
                <div class="contact-items">
                  <span class="item-left">业务部门：0471-3121519</span>
                  <span class="item-right">综合管理部：0471-3121516</span>
                </div>
              </div>
              <div class="contact-row">
                <img :src="emailIcon" alt="email" class="icon" />
                <div class="contact-items">
                  <span class="item-left">邮箱：sjtunmg@sjtu.edu.cn</span>
                  <span class="item-right">邮编：010000</span>
                </div>
              </div>
              <p>
                <img :src="addressIcon" alt="address" class="icon" />
                <span> {{ contactInfo.address }}</span>
              </p>
            </div>
          </div>
        </div>
        <div class="nav-section-right">
          <div class="qr-codes">
            <div class="qr-code">
              <img :src="qr1" alt="二维码" />
            </div>
            <div class="qr-code">
              <img :src="qr2" alt="二维码" />
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="divider2"></div>
    <div class="copyright">
      <p>Copyright © 内蒙古研究院 版权所有</p>
    </div>
  </footer>
</template>

<style lang="scss" scoped>
.footer {
  background: #0264c4;
  color: white;
  display: flex;
  flex-direction: column;
  width: 100%;
  overflow: hidden;
  padding: 38px 0 0;

  &-content {
    max-width: 1600px;
    width: 100%;
    margin: 0 auto;
    padding: 0 40px;
    display: flex;
    flex-direction: column;
    flex: 1;
    box-sizing: border-box;
  }
}

.top-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  box-sizing: border-box;
  flex-direction: row;

  .logo img {
    width: clamp(200px, 30vw, 332px);
    height: auto;
    object-fit: contain;
  }

  .institution {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;

    img {
      height: 60px;
      object-fit: contain;
    }

    .icp {
      margin: 0;
      font-size: 12px;

      a {
        color: #fff;
        text-decoration: none;
      }
    }
  }
}

.divider,
.divider2 {
  width: 100%;
  height: 1px;
  background: rgba(255, 255, 255, 0.1);
  margin: 40px auto;
}

.divider2 {
  margin: 20px auto;
}

.nav-section {
  display: flex;
  justify-content: space-between;
  gap: 40px;
  flex-wrap: wrap;
}

.nav-section-left {
  flex: 1;
  min-width: 300px;
}

.nav-section-right {
  display: flex;
  justify-content: flex-end;
}

.footer-nav {
  display: flex;
  gap: 40px;
  padding: 10px 0 30px;
  flex-wrap: wrap;

  .nav-item {
    color: white;
    text-decoration: none;
    font-size: clamp(14px, 1.2vw, 16px);
    white-space: nowrap;

    &:hover {
      opacity: 0.8;
    }
  }
}

.info-section {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding-bottom: 0;
}

.contact-info {
  display: flex;
  flex-direction: column;
  gap: 20px;

  p {
    margin: 0;
    font-size: clamp(14px, 1.2vw, 16px);
    color: rgba(255, 255, 255, 0.9);
    display: flex;
    align-items: center;

    img {
      width: 18px;
      height: 18px;
      margin-right: 8px;
      flex-shrink: 0;
    }

    span {
      margin-left: 6px;
      font-size: clamp(14px, 1.2vw, 16px);
    }
  }
}

.contact-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.contact-items {
  display: flex;
  gap: 30px;
  color: rgba(255, 255, 255, 0.9);
  font-size: clamp(14px, 1.2vw, 16px);
  flex: 1;

  span {
    white-space: nowrap;
    font-size: clamp(14px, 1.2vw, 16px);
  }

  .item-left {
    width: 240px;
    flex-shrink: 0;
  }

  .item-right {
    flex-shrink: 0;
  }
}

.icon {
  width: 18px;
  height: 18px;
  margin-right: 8px;
  flex-shrink: 0;
}

.qr-codes {
  display: flex;
  gap: 30px;

  .qr-code img {
    width: clamp(100px, 12vw, 140px);
    height: clamp(100px, 12vw, 140px);
  }
}

.copyright {
  text-align: center;
  padding: 20px;

  p {
    margin: 0;
    font-size: clamp(14px, 1.2vw, 16px);
    color: rgba(255, 255, 255, 0.7);
  }
}

@media (max-width: 1600px) {
  .footer-content {
    padding: 38px 40px 0;
  }
}

@media (max-width: 1470px) {
  .footer {
    min-height: 450px;
  }

  .footer-content {
    max-width: 1400px;
    padding: 30px 30px 0;
  }

  .top-section {
    .logo img {
      width: 280px;
    }

    .institution img {
      width: 38px;
    }
  }

  .divider,
  .divider2 {
    margin: 30px auto;
  }

  .nav-section {
    gap: 30px;
  }

  .footer-nav {
    gap: 30px;
    padding: 8px 0 24px;

    .nav-item {
      font-size: 14px;
    }
  }

  .contact-info {
    gap: 16px;

    p {
      font-size: 13px;
    }
  }

  .phone-info {
    .departments {
      gap: 16px;
    }
  }

  .qr-codes {
    gap: 24px;

    .qr-code img {
      width: 120px;
      height: 120px;
    }
  }

  .copyright p {
    font-size: 13px;
  }
}

@media (max-width: 1200px) {
  .footer-nav {
    gap: 30px;
  }

  .nav-section {
    gap: 30px;
  }
}

@media (max-width: 768px) {
  .footer {
    padding: 30px 0 20px;
  }

  .footer-content {
    padding: 30px 20px 0;
  }

  .top-section {
    flex-direction: row;
    gap: 20px;
    align-items: center;
    justify-content: space-between;

    .logo img {
      width: 200px;
      max-width: 100%;
    }

    .institution img {
      width: 30px;
    }
  }

  .divider,
  .divider2 {
    margin: 20px auto;
  }

  .nav-section {
    flex-direction: column;
    gap: 30px;
    width: 100%;
  }

  .nav-section-left {
    min-width: auto;
    width: 100%;
  }

  .footer-nav {
    gap: 20px;
    padding: 10px 0 20px;
    width: 100%;
    justify-content: flex-start;
  }

  .contact-info {
    gap: 15px;
    width: 100%;

    p {
      width: 100%;
      word-break: break-word;
    }
  }

  .phone-info {
    width: 100%;
    align-items: flex-start;
    flex-wrap: nowrap;

    .departments {
      display: flex;
      gap: 20px;
      flex-direction: row;
      flex-wrap: nowrap;
      width: auto;
      overflow-x: auto;
      -webkit-overflow-scrolling: touch;

      p {
        white-space: nowrap;
        width: auto;
        flex-shrink: 0;
      }
    }
  }

  .nav-section-right {
    justify-content: center;
    width: 100%;
  }

  .qr-codes {
    gap: 20px;
    justify-content: center;
    width: 100%;

    .qr-code img {
      width: 100px;
      height: 100px;
    }
  }

  .contact-items {
    flex-direction: column;
    gap: 15px;

    .item-left {
      width: auto;
    }
  }
}

@media (max-width: 480px) {
  .footer-content {
    padding: 20px 16px 0;
  }

  .contact-info p {
    font-size: 12px;
  }

  .phone-info {
    .departments {
      gap: 10px;
    }
  }
}
</style>
